import { useEffect } from "react";
import { useParams, useNavigate, Navigate, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Seo } from "@/components/Seo";
import { Header } from "@/components/Header";
import { Footer } from "@/components/Footer";
import { Reveal } from "@/components/Reveal";
import { MARKETS, MARKET_LIST } from "@/data/markets";
import { ArrowRight, ArrowUpRight } from "lucide-react";

export default function MarketPage() {
  const { slug } = useParams();
  const nav = useNavigate();
  const market = MARKETS[slug];

  useEffect(() => { window.scrollTo(0, 0); }, [slug]);
  if (!market) return <Navigate to="/" replace />;

  const goContact = () => { nav("/"); setTimeout(() => document.getElementById("contact")?.scrollIntoView({ behavior: "smooth" }), 120); };

  const jsonLd = {
    "@context": "https://schema.org",
    "@type": "Service",
    serviceType: "HR Governance & People Risk Consulting",
    areaServed: market.region,
    provider: { "@type": "ProfessionalService", name: "Irina Ishik HR Governance & People Risk Consulting" },
    description: market.meta,
  };

  return (
    <div data-testid={`market-page-${market.slug}`}>
      <Seo title={market.hero} description={market.meta} path={`/markets/${market.slug}`} jsonLd={jsonLd} />
      <Header />
      <main className="pt-36 pb-32 px-6 md:px-12 min-h-screen">
        <div className="max-w-5xl mx-auto">
          <p className="font-mono text-[11px] tracking-[0.35em] text-gold uppercase mb-6">Markets · {market.region}</p>
          <motion.h1
            initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8 }}
            className="font-serif font-light text-5xl md:text-7xl tracking-tight leading-[1.02] mb-10 max-w-4xl"
          >
            {market.hero}
          </motion.h1>
          <p className="text-muted2 text-lg leading-relaxed max-w-3xl mb-16">{market.intro}</p>

          <div className="border-t border-white/15">
            {market.highlights.map((h, i) => (
              <Reveal key={i} delay={i * 0.05}>
                <div className="border-b border-white/15 py-8 flex items-start gap-6" data-testid={`market-highlight-${i}`}>
                  <span className="font-mono text-sm text-gold/70 shrink-0 pt-1">{String(i + 1).padStart(2, "0")}</span>
                  <p className="text-bone text-lg md:text-xl font-light leading-relaxed">{h}</p>
                </div>
              </Reveal>
            ))}
          </div>

          <Reveal>
            <div className="mt-16 flex flex-col sm:flex-row gap-4">
              <button data-testid="market-book-cta" onClick={goContact}
                className="group inline-flex items-center gap-3 font-sans text-xs uppercase tracking-[0.2em] bg-gold text-ink px-8 py-4 hover:bg-goldhover transition-colors">
                Book a Confidential Consultation
                <ArrowRight size={16} className="group-hover:translate-x-1 transition-transform" />
              </button>
              <Link to="/insights" data-testid="market-insights-link"
                className="inline-flex items-center gap-2 font-sans text-xs uppercase tracking-[0.2em] border border-white/20 text-bone px-8 py-4 hover:border-gold hover:text-gold transition-colors">
                Read Insights
              </Link>
            </div>
          </Reveal>

          <div className="mt-24 pt-10 border-t border-white/15">
            <p className="font-mono text-[10px] tracking-[0.3em] uppercase text-muted2 mb-6">Other Markets</p>
            <div className="flex flex-wrap gap-4">
              {MARKET_LIST.filter((m) => m.slug !== market.slug).map((m) => (
                <Link key={m.slug} to={`/markets/${m.slug}`} data-testid={`market-link-${m.slug}`}
                  className="group inline-flex items-center gap-2 font-serif text-xl hover:text-gold transition-colors">
                  {m.region} <ArrowUpRight size={16} className="text-muted2 group-hover:text-gold transition-colors" />
                </Link>
              ))}
            </div>
          </div>
        </div>
      </main>
      <Footer />
    </div>
  );
}
